// Utilities for maintaining the cached `s3LabelUrl` on attendees: bulk
// invalidation, the "has a cached label" predicate, and the single write
// path that sets the field.
//
// The invalidation side centralizes the batching + per-batch error
// isolation logic shared by the per-event and per-group bulk clears, plus
// the predicate used to pre-filter so the cleared count reflects real
// writes.
//
// Public bulk-clear helpers live in `events.ts` (`bulkClearLabelUrlsForEvent`)
// and `groups.ts` (`clearLabelUrlsForGroup`) next to the entities they
// audit-log against. This module is the shared implementation — its
// exports are consumed by those helpers, by the admin dialog that builds
// the input ref list (`hasCachedLabel`), and by the check-in page's print
// handler (`cacheLabelUrl`).

import { deleteField, doc, updateDoc, writeBatch } from 'firebase/firestore';
import { attendeesCollectionRef, db } from '../firebase';
import { FIRESTORE_BATCH_LIMIT } from '../constants';

export type BulkClearLabelsResult = {
  cleared: number;
  failed: number;
};

// True when an attendee/doc has a cached label URL we'd want to invalidate.
// `s3LabelUrl` is sometimes absent and sometimes set to a string; legacy
// docs may carry an empty string. The bulk-clear paths only act on docs
// where the field is a non-empty string so the cleared count never
// includes no-op `deleteField()` writes against an absent field.
export const hasCachedLabel = (v: unknown): v is string =>
  typeof v === 'string' && v.length > 0;

// Issues `deleteField()` writes on `s3LabelUrl` for every supplied attendee
// ref, batched at `FIRESTORE_BATCH_LIMIT`. Each batch is committed in its
// own try/catch so one bad ref (e.g. hard-deleted between the caller's
// snapshot and this commit) only loses up to one batch's worth of writes
// instead of every remaining attendee. Returns the cleared/failed counts
// so the caller can decide whether to surface a partial-failure warning.
// `context` is prefixed onto the per-batch error log for debuggability.
export const clearLabelUrlsForRefs = async (
  refs: string[],
  context: string,
): Promise<BulkClearLabelsResult> => {
  if (refs.length === 0) return { cleared: 0, failed: 0 };

  let cleared = 0;
  let failed = 0;

  for (let i = 0; i < refs.length; i += FIRESTORE_BATCH_LIMIT) {
    const chunk = refs.slice(i, i + FIRESTORE_BATCH_LIMIT);
    const batch = writeBatch(db);
    chunk.forEach((id) => {
      batch.update(doc(attendeesCollectionRef, id), {
        s3LabelUrl: deleteField(),
      });
    });
    try {
      await batch.commit();
      cleared += chunk.length;
    } catch (err) {
      console.error(`${context}: batch of ${chunk.length} failed`, err);
      failed += chunk.length;
    }
  }

  return { cleared, failed };
};

// Persists a freshly-generated label URL onto an attendee.
//
// This is the ONLY place in the app that writes a value to `s3LabelUrl`;
// everywhere else the field is cleared. It's needed because the label
// backend only writes the URL back itself when the request carries an
// `attendeeId`, and the folded-class-line payload deliberately omits one
// (see `buildInlineLabelPdfBody` in `lib/printer-api.ts`). Without this,
// the fold path would regenerate the PDF on every single print.
//
// Deliberately NOT routed through `updateAttendee`: that helper forces
// `s3LabelUrl: deleteField()` onto every payload, so it can only ever
// clear this field. No activity-log entry is written either — `s3LabelUrl`
// is on the `diffFields` exclude list, so there would be nothing to log.
//
// Caching is best-effort: a failure here costs a regenerated PDF on the
// next print, so it's logged and swallowed rather than surfaced to the
// volunteer mid-check-in. Returns whether the write landed.
export const cacheLabelUrl = async (
  attendeeRef: string,
  url: string,
): Promise<boolean> => {
  if (!attendeeRef || !hasCachedLabel(url)) return false;
  try {
    await updateDoc(doc(attendeesCollectionRef, attendeeRef), {
      s3LabelUrl: url,
    });
    return true;
  } catch (err) {
    console.error(
      `cacheLabelUrl: failed to cache label for ${attendeeRef}`,
      err,
    );
    return false;
  }
};
